import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SyncBadge, ExternalActionBadge } from "@/components/common/SyncBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, money, pct, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function PhoneNumbers() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [providerOpen, setProviderOpen] = useState(false);
  const [releasing, setReleasing] = useState(null);
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState({ provider: "mmdsmart", country: "US", number_type: "local", area_code: "", contains: "" });
  const [searchResult, setSearchResult] = useState(null);
  const [draft, setDraft] = useState({
    phone: "", country: "AL", caller_id_name: "", concurrent_limit: 5, daily_limit: 400,
    monthly_cost: 5, provider: "mmdsmart",
  });

  const { data, isLoading } = useQuery({ queryKey: ["numbers"], queryFn: () => get("/phone-numbers") });

  const create = useMutation({
    mutationFn: () => api.post("/phone-numbers", { ...draft, languages: ["sq"] }),
    onSuccess: () => {
      toast.success("Phone number added");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["numbers"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const syncNumber = useMutation({
    mutationFn: (id) => api.post(`/phone-numbers/${id}/sync`),
    onSuccess: ({ data: r }) => {
      if (r.sync_status === "synced") toast.success("Number synchronised with the provider");
      else toast.error(r.error?.reason || r.error?.message || "Synchronisation failed");
      queryClient.invalidateQueries({ queryKey: ["numbers"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const providerList = useQuery({
    queryKey: ["provider-numbers", search.provider],
    queryFn: () => get(`/phone-numbers/provider/list?provider=${search.provider}`),
    enabled: providerOpen,
    retry: false,
  });

  const runSearch = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams({
        provider: search.provider, country: search.country, number_type: search.number_type,
      });
      if (search.area_code) params.set("area_code", search.area_code);
      if (search.contains) params.set("contains", search.contains);
      return get(`/phone-numbers/provider/search?${params.toString()}`);
    },
    onSuccess: (r) => {
      setSearchResult(r);
      toast.success(`${(r.items || []).length} available numbers found`);
    },
    onError: (e) => {
      setSearchResult(null);
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail?.reason || apiError(e));
    },
  });

  const importNumber = useMutation({
    mutationFn: (row) => api.post("/phone-numbers/provider/import", {
      phone: row.phone, provider: search.provider, country: row.country,
      caller_id_name: row.label || "",
    }),
    onSuccess: () => {
      toast.success("Number imported and synchronised");
      providerList.refetch();
      queryClient.invalidateQueries({ queryKey: ["numbers"] });
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.message || e.response?.data?.detail || apiError(e)),
  });

  const importToVapi = useMutation({
    mutationFn: (id) => api.post(`/phone-numbers/${id}/import-to-vapi`),
    onSuccess: ({ data: r }) => {
      toast.success(`Number registered in Vapi (${r.voice_provider_resource_id.slice(0, 8)}…)`);
      queryClient.invalidateQueries({ queryKey: ["numbers"] });
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.message || e.response?.data?.detail?.reason || apiError(e)),
  });

  const release = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/phone-numbers/${id}/release`, {
      reason, typed_confirmation: typed }),
    onSuccess: () => {
      toast.success("Number released at the provider and archived");
      setReleasing(null); setTyped("");
      queryClient.invalidateQueries({ queryKey: ["numbers"] });
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.reason || e.response?.data?.detail || apiError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/phone-numbers/${id}`, { status }),
    onSuccess: () => {
      toast.success("Number updated");
      queryClient.invalidateQueries({ queryKey: ["numbers"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      <PageHeader
        testId="phone-numbers-page"
        title="Phone numbers"
        subtitle="Outbound caller IDs, limits and reputation signals"
        actions={
          can("phone_numbers", "write") ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setProviderOpen(true)} data-testid="provider-numbers-button">
                Search &amp; import
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)} data-testid="add-number-button">
                Add number
              </Button>
            </div>
          ) : null
        }
      />

      <Section testId="numbers-section" title={`${(data || []).length} numbers`}>
        <DataTable
          testId="numbers-table"
          rowTestId="number-row"
          loading={isLoading}
          columns={[
            { key: "phone", label: "Number", render: (r) => <span className="font-mono text-xs font-medium text-slate-800">{r.phone}</span> },
            { key: "provider", label: "Provider", render: (r) => title(r.provider) },
            { key: "country", label: "Country" },
            { key: "languages", label: "Languages", render: (r) => (r.languages || []).join(", ").toUpperCase() },
            { key: "caller_id_name", label: "Caller ID" },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status === "active" ? "running" : "paused"} label={title(r.status)} /> },
            { key: "sync_status", label: "Provider sync", render: (r) => (
              <div className="flex flex-col gap-1">
                <SyncBadge status={r.sync_status || "not_connected"} testId={`number-sync-${r.id}`} />
                <ExternalActionBadge action={r.external_action_required} />
              </div>
            ) },
            { key: "provider_resource_id", label: "Provider ID", render: (r) => <span className="font-mono text-[11px]">{r.provider_resource_id || "—"}</span> },
            { key: "voice_provider_resource_id", label: "In Vapi", render: (r) => (
              r.voice_provider_resource_id
                ? <StatusBadge value="completed" label="Registered" testId={`number-in-vapi-${r.id}`} />
                : <StatusBadge value="draft" label="Not imported" testId={`number-in-vapi-${r.id}`} />
            ) },
            { key: "concurrent_limit", label: "Concurrency" },
            { key: "daily_limit", label: "Daily limit" },
            { key: "calls_today", label: "Calls today" },
            { key: "answer_rate", label: "Answer rate", render: (r) => pct(r.answer_rate) },
            { key: "failure_rate", label: "Failure rate", render: (r) => pct(r.failure_rate) },
            {
              key: "spam_warning",
              label: "Reputation",
              render: (r) => (r.spam_warning ? <StatusBadge value="failed" label="Spam risk" /> : <StatusBadge value="completed" label="Healthy" />),
            },
            {
              key: "assigned_campaigns",
              label: "Campaigns",
              render: (r) => (r.assigned_campaigns || []).map((c) => c.name).join(", ") || "—",
            },
            { key: "monthly_cost", label: "Monthly cost", render: (r) => money(r.monthly_cost) },
            {
              key: "actions",
              label: "",
              render: (r) =>
                can("phone_numbers", "write") ? (
                  <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => syncNumber.mutate(r.id)} data-testid={`number-sync-btn-${r.id}`}>Sync</Button>
                  <Button size="sm" variant="ghost" className="text-indigo-600" disabled={importToVapi.isPending} onClick={() => importToVapi.mutate(r.id)} data-testid={`number-import-vapi-${r.id}`}>
                    {r.voice_provider_resource_id ? "Re-import to Vapi" : "Import to Vapi"}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => { setReleasing(r); setTyped(""); }} data-testid={`number-release-${r.id}`}>Release</Button>
                  <Switch
                    data-testid={`number-toggle-${r.id}`}
                    checked={r.status === "active"}
                    onCheckedChange={(checked) => toggle.mutate({ id: r.id, status: checked ? "active" : "paused" })}
                  />
                  </div>
                ) : null,
            },
          ]}
          rows={data || []}
          empty={{ title: "No phone numbers", description: "Add an outbound number before launching campaigns." }}
        />
      </Section>

      <p className="text-xs text-slate-500">
        Caller-ID rotation designed to bypass spam protection is intentionally not implemented.
      </p>

      <ConfirmDialog
        open={!!releasing}
        onOpenChange={(o) => !o && setReleasing(null)}
        destructive
        requireReason
        reasonLabel="Release reason (audited)"
        title={`Release ${releasing?.phone}?`}
        description={`This permanently releases the number at the provider. Type ${releasing?.phone} below in the field next to it and give a reason. Numbers assigned to active campaigns cannot be released.`}
        confirmLabel="Release number"
        onConfirm={(reason) => release.mutateAsync({ id: releasing.id, reason })}
        testId="number-release-dialog"
      />

      {releasing ? (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 shadow-md">
          <Label>Type the full number to confirm</Label>
          <Input data-testid="number-release-typed" className="mt-1 w-64" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={releasing.phone} />
        </div>
      ) : null}

      <Dialog open={providerOpen} onOpenChange={setProviderOpen}>
        <DialogContent className="max-w-4xl bg-white" data-testid="provider-numbers-dialog">
          <DialogHeader><DialogTitle>Provider numbers — search &amp; import</DialogTitle></DialogHeader>

          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="purchase-disabled-notice">
            Purchase disabled in current phase. You can search availability and import numbers that
            already exist in the provider account; buying new numbers is intentionally blocked.
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select value={search.provider} onValueChange={(v) => setSearch({ ...search, provider: v })}>
                <SelectTrigger data-testid="search-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="twilio">Twilio</SelectItem>
                  <SelectItem value="mmdsmart">MMDSmart</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Select value={search.country} onValueChange={(v) => setSearch({ ...search, country: v })}>
                <SelectTrigger data-testid="search-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={search.number_type} onValueChange={(v) => setSearch({ ...search, number_type: v })}>
                <SelectTrigger data-testid="search-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="local">Local</SelectItem>
                  <SelectItem value="toll_free">Toll free</SelectItem>
                  <SelectItem value="mobile">Mobile</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Area code</Label>
              <Input data-testid="search-area-code" value={search.area_code} onChange={(e) => setSearch({ ...search, area_code: e.target.value })} />
            </div>
            <div className="flex items-end">
              <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={runSearch.isPending} onClick={() => runSearch.mutate()} data-testid="search-numbers-submit">
                {runSearch.isPending ? "Searching…" : "Search"}
              </Button>
            </div>
          </div>

          <div className="max-h-56 overflow-auto rounded-md border border-slate-200" data-testid="search-results">
            {(searchResult?.items || []).length ? (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Number</th>
                    <th className="px-2 py-1.5 text-left">Locality</th>
                    <th className="px-2 py-1.5 text-left">Monthly</th>
                    <th className="px-2 py-1.5 text-left">Setup</th>
                    <th className="px-2 py-1.5 text-left">Regulatory</th>
                    <th className="px-2 py-1.5 text-left" />
                  </tr>
                </thead>
                <tbody>
                  {searchResult.items.map((r) => (
                    <tr key={r.phone} className="border-t border-slate-100" data-testid={`search-row-${r.phone}`}>
                      <td className="px-2 py-1.5 font-mono">{r.phone}</td>
                      <td className="px-2 py-1.5">{r.locality || r.region || r.country}</td>
                      <td className="px-2 py-1.5">{r.monthly_price != null ? money(r.monthly_price) : "—"}</td>
                      <td className="px-2 py-1.5">{r.setup_price != null ? money(r.setup_price) : "—"}</td>
                      <td className="px-2 py-1.5">{r.address_required && r.address_required !== "none" ? r.address_required : "—"}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" variant="ghost" disabled title="Purchase disabled in current phase" data-testid={`purchase-disabled-${r.phone}`}>
                          Purchase disabled
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-3 text-xs text-slate-500">
                No search results yet. Availability results are read-only in this phase.
              </p>
            )}
          </div>

          {searchResult?.regulatory?.regulations?.length ? (
            <p className="text-xs text-slate-600" data-testid="regulatory-note">
              Regulatory requirements: {searchResult.regulatory.regulations.slice(0, 3)
                .map((r) => `${r.friendly_name} (${r.end_user_type})`).join(" · ")}
            </p>
          ) : null}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Numbers already owned at the provider</Label>
              <Button size="sm" variant="outline" onClick={() => providerList.refetch()} data-testid="provider-list-refresh">
                Sync list
              </Button>
            </div>
            <div className="max-h-52 overflow-auto rounded-md border border-slate-200" data-testid="provider-owned-list">
              {providerList.isError ? (
                <p className="p-3 text-xs text-rose-600">
                  {providerList.error?.response?.data?.detail?.message
                    || providerList.error?.response?.data?.detail?.reason
                    || "Provider is not connected. Add credentials in Provider Center."}
                </p>
              ) : (providerList.data?.items || []).length ? (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Number</th>
                      <th className="px-2 py-1.5 text-left">Label</th>
                      <th className="px-2 py-1.5 text-left">Voice URL</th>
                      <th className="px-2 py-1.5 text-left">Status</th>
                      <th className="px-2 py-1.5 text-left" />
                    </tr>
                  </thead>
                  <tbody>
                    {providerList.data.items.map((r) => (
                      <tr key={r.provider_id || r.phone} className="border-t border-slate-100" data-testid={`provider-number-${r.phone}`}>
                        <td className="px-2 py-1.5 font-mono">{r.phone}</td>
                        <td className="px-2 py-1.5">{r.label || "—"}</td>
                        <td className="px-2 py-1.5 max-w-[14rem] truncate">{r.voice_url || "—"}</td>
                        <td className="px-2 py-1.5">{r.imported ? "Imported" : "Not imported"}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button size="sm" variant="ghost" disabled={importNumber.isPending} onClick={() => importNumber.mutate(r)} data-testid={`import-number-${r.phone}`}>
                            {r.imported ? "Re-sync" : "Import"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="p-3 text-xs text-slate-500">
                  {providerList.isFetching ? "Loading provider numbers…" : "No numbers found in the provider account."}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Add outbound number</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Phone number</Label>
              <Input data-testid="number-phone-input" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="+355 44 500 000" />
            </div>
            <div className="space-y-1.5">
              <Label>Caller ID name</Label>
              <Input data-testid="number-callerid-input" value={draft.caller_id_name} onChange={(e) => setDraft({ ...draft, caller_id_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Select value={draft.country} onValueChange={(v) => setDraft({ ...draft, country: v })}>
                <SelectTrigger data-testid="number-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Concurrent-call limit</Label>
              <Input data-testid="number-concurrency-input" type="number" value={draft.concurrent_limit} onChange={(e) => setDraft({ ...draft, concurrent_limit: Number(e.target.value) })} />
            </div>
            <div className="space-y-1.5">
              <Label>Daily call limit</Label>
              <Input data-testid="number-daily-input" type="number" value={draft.daily_limit} onChange={(e) => setDraft({ ...draft, daily_limit: Number(e.target.value) })} />
            </div>
            <div className="space-y-1.5">
              <Label>Monthly cost (USD)</Label>
              <Input data-testid="number-cost-input" type="number" step="0.5" value={draft.monthly_cost} onChange={(e) => setDraft({ ...draft, monthly_cost: Number(e.target.value) })} />
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={!draft.phone || create.isPending} onClick={() => create.mutate()} data-testid="number-create-submit">
              Add number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}